import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Building2, Users, Bed, Bath, Plus, Edit, Trash2, AlertTriangle, CheckCircle, ChevronRight, Home as HomeIcon, ArrowLeft, MoreVertical, XCircle } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PropertyTypeBadge } from '../components/PropertyTypeSelector';
import api from '../lib/api';

const ROOM_STATUSES = {
  available: { label: 'Available', color: 'green', icon: CheckCircle },
  occupied: { label: 'Occupied', color: 'blue', icon: Users },
  offline: { label: 'Offline', color: 'slate', icon: XCircle },
  reserved: { label: 'Reserved', color: 'orange', icon: AlertTriangle }
};

export default function RoomManagement() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  
  const [property, setProperty] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [communalAreas, setCommunalAreas] = useState([]);
  const [occupancy, setOccupancy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [newRoom, setNewRoom] = useState({
    name: '',
    rent_amount: 0,
    ensuite: false,
    furnished: true
  });
  
  useEffect(() => {
    loadData();
  }, [propertyId]);
  
  const loadData = async () => {
    try {
      setLoading(true);
      const propRes = await api.get(`/properties/${propertyId}`);
      setProperty(propRes.data);
      const roomsRes = await api.get(`/property-types/properties/${propertyId}/rooms`);
      setRooms(roomsRes.data.rooms || []);
      const areasRes = await api.get(`/property-types/properties/${propertyId}/communal-areas`);
      setCommunalAreas(areasRes.data.areas || []);
      const occRes = await api.get(`/property-types/properties/${propertyId}/occupancy`);
      setOccupancy(occRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddRoom = async () => {
    try {
      await api.post(`/property-types/properties/${propertyId}/rooms`, newRoom);
      setShowAddRoom(false);
      setNewRoom({ name: '', rent_amount: 0, ensuite: false, furnished: true });
      loadData();
    } catch (err) {
      console.error('Failed to add room:', err);
    }
  };
  
  const handleUpdateStatus = async (roomId, status) => {
    try {
      await api.put(`/property-types/rooms/${roomId}/status`, { status });
      loadData();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };
  
  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm('Are you sure you want to delete this room?')) return;
    try {
      await api.delete(`/property-types/rooms/${roomId}`);
      loadData();
    } catch (err) {
      console.error('Failed to delete room:', err);
      alert(err.response?.data?.detail || 'Failed to delete room');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-600 hover:text-primary mb-4">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">{property?.address?.street || 'Property'}</h1>
                <PropertyTypeBadge type={property?.property_type || 'standard'} />
              </div>
              <p className="text-slate-600">Room & Occupancy Management</p>
            </div>
            <Button onClick={() => setShowAddRoom(true)}><Plus className="w-4 h-4 mr-2" />Add Room</Button>
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-6 py-8">
        {occupancy && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="p-4 text-center">
              <div className="text-3xl font-bold text-primary">{occupancy.total_rooms}</div>
              <div className="text-sm text-slate-600">Total Rooms</div>
            </Card>
            <Card className="p-4 text-center">
              <div className="text-3xl font-bold text-blue-600">{occupancy.occupied_rooms}</div>
              <div className="text-sm text-slate-600">Occupied</div>
            </Card>
            <Card className="p-4 text-center">
              <div className="text-3xl font-bold text-green-600">{occupancy.available_rooms}</div>
              <div className="text-sm text-slate-600">Available</div>
            </Card>
            <Card className="p-4 text-center">
              <div className="text-3xl font-bold text-slate-400">{occupancy.offline_rooms}</div>
              <div className="text-sm text-slate-600">Offline</div>
            </Card>
            <Card className="p-4 text-center">
              <div className="text-3xl font-bold">{occupancy.occupancy_rate}%</div>
              <div className="text-sm text-slate-600">Occupancy Rate</div>
            </Card>
          </div>
        )}
        
        <h2 className="text-lg font-semibold mb-4">Rooms ({rooms.length})</h2>
        {rooms.length === 0 ? (
          <Card className="p-8 text-center">
            <Bed className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600 mb-4">No rooms added yet</p>
            <Button onClick={() => setShowAddRoom(true)}><Plus className="w-4 h-4 mr-2" />Add First Room</Button>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {rooms.map(room => {
              const statusInfo = ROOM_STATUSES[room.status] || ROOM_STATUSES.available;
              const StatusIcon = statusInfo.icon;
              return (
                <Card key={room.id} className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold">{room.name}</h3>
                      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-100">
                        <StatusIcon className="w-3 h-3" />{statusInfo.label}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold">£{room.rent_amount}</div>
                      <div className="text-xs text-slate-500">/{room.rent_frequency}</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {room.ensuite && <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">Ensuite</span>}
                    {room.furnished && <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded">Furnished</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <select value={room.status} onChange={(e) => handleUpdateStatus(room.id, e.target.value)} className="flex-1 text-sm border rounded px-2 py-1">
                      <option value="available">Available</option>
                      <option value="occupied">Occupied</option>
                      <option value="offline">Offline</option>
                      <option value="reserved">Reserved</option>
                    </select>
                    <button onClick={() => handleDeleteRoom(room.id)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      
      {showAddRoom && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4 p-6">
            <h2 className="text-xl font-bold mb-4">Add New Room</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Room Name</label>
                <input type="text" value={newRoom.name} onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })} placeholder="e.g., Room 1" className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Monthly Rent (£)</label>
                <input type="number" value={newRoom.rent_amount} onChange={(e) => setNewRoom({ ...newRoom, rent_amount: parseFloat(e.target.value) || 0 })} className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2"><input type="checkbox" checked={newRoom.ensuite} onChange={(e) => setNewRoom({ ...newRoom, ensuite: e.target.checked })} /><span className="text-sm">Ensuite</span></label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={newRoom.furnished} onChange={(e) => setNewRoom({ ...newRoom, furnished: e.target.checked })} /><span className="text-sm">Furnished</span></label>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowAddRoom(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleAddRoom} disabled={!newRoom.name} className="flex-1">Add Room</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
